import React, { useState } from 'react';
import { API_ENDPOINTS } from '../config/api';

const AuthModal = ({ onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '' 
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const disposableProviders = ['10minutemail.com', 'tempmail.org', 'guerrillamail.com', 'mailinator.com'];
    
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Invalid email format';
    
    const domain = email.split('@')[1]?.toLowerCase();
    if (disposableProviders.includes(domain)) return 'Disposable email addresses not allowed';
    
    return null;
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (!password) return ['Password is required'];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('One special character');
    if (password !== password.trim()) errors.push('No leading/trailing spaces');
    
    const commonPasswords = ['password', '123456789', 'qwerty123', 'password123'];
    if (commonPasswords.includes(password.toLowerCase())) errors.push('Password is too common');
    
    return errors.length > 0 ? errors : null;
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 10;
    if (/[A-Z]/.test(password)) score += 20;
    if (/[a-z]/.test(password)) score += 20;
    if (/[0-9]/.test(password)) score += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 15;
    return Math.min(score, 100);
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = name === 'email' ? value.trim().toLowerCase() : value;
    
    setLoginData(prev => ({ ...prev, [name]: trimmedValue }));
    
    // Clear errors for login
    const newErrors = { ...errors };
    delete newErrors[name];
    setErrors(newErrors);
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = name === 'email' ? value.trim().toLowerCase() : value;
    
    setSignupData(prev => ({ ...prev, [name]: trimmedValue }));
    
    // Real-time validation
    const newErrors = { ...errors };
    
    if (name === 'email') {
      const emailError = validateEmail(trimmedValue);
      if (emailError) newErrors.email = emailError;
      else delete newErrors.email;
    }
    
    if (name === 'password') {
      const passwordErrors = validatePassword(trimmedValue);
      if (passwordErrors) newErrors.password = passwordErrors;
      else delete newErrors.password;
      
      setPasswordStrength(calculatePasswordStrength(trimmedValue));
      
      // Revalidate confirm password if it exists
      if (signupData.confirmPassword && trimmedValue !== signupData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else if (signupData.confirmPassword) {
        delete newErrors.confirmPassword;
      }
    }
    
    if (name === 'confirmPassword') {
      if (!trimmedValue) newErrors.confirmPassword = 'Confirm password is required';
      else if (trimmedValue !== signupData.password) newErrors.confirmPassword = 'Passwords do not match';
      else delete newErrors.confirmPassword;
    }
    
    if (name === 'name') {
      if (!trimmedValue.trim()) newErrors.name = 'Name is required';
      else delete newErrors.name;
    }
    
    setErrors(newErrors);
  };

  const handlePaste = (e) => {
    if (e.target.name === 'confirmPassword') {
      e.preventDefault();
    }
  };

  const isSignupFormValid = () => {
    return signupData.name && signupData.email && signupData.password && 
           signupData.confirmPassword && Object.keys(errors).length === 0;
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 30) return 'bg-red-500';
    if (passwordStrength < 60) return 'bg-yellow-500';
    if (passwordStrength < 80) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 30) return 'Weak';
    if (passwordStrength < 60) return 'Fair';
    if (passwordStrength < 80) return 'Good';
    return 'Strong';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_ENDPOINTS.auth}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userId', data.user.id); // Store userId
        
        // Dispatch custom event for VowBot to listen
        window.dispatchEvent(new CustomEvent('userLoggedIn', { 
          detail: { userId: data.user.id, user: data.user } 
        }));
        
        onSuccess(data.user);
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          setErrors({ general: data.error || 'Login failed' });
        }
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_ENDPOINTS.auth}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Clear signup form and reset state
        setSignupData({ name: '', email: '', password: '', confirmPassword: '' });
        setPasswordStrength(0);
        setErrors({ general: 'Account created successfully! Please login.' });
        
        setTimeout(() => {
          setActiveTab('login');
          setErrors({});
        }, 2000);
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          setErrors({ general: data.error || 'Registration failed' });
        }
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Forgot password - send reset email
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const emailError = validateEmail(forgotEmail);
    if (emailError) {
      setErrors({ forgotEmail: emailError });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.auth}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json();

      if (response.ok) {
        setForgotSent(true);
      } else {
        setErrors({ general: data.error || 'Failed to send reset email' });
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setErrors({});
    setPasswordStrength(0);
    setShowForgot(false);
    setForgotSent(false);
    setForgotEmail('');
    
    // Clear forms when switching tabs
    if (tab === 'login') {
      setLoginData({ email: '', password: '' });
    } else {
      setSignupData({ name: '', email: '', password: '', confirmPassword: '' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-white via-rose-50 to-pink-50 rounded-2xl shadow-2xl p-6 w-full max-w-md mx-auto relative border border-rose-100 transform transition-all duration-300 scale-100 hover:scale-[1.02] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-rose-400 via-pink-400 to-purple-400 rounded-t-2xl"></div>
        <div className="absolute -top-1 -right-1 w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-15 animate-pulse"></div>
        
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-rose-500 text-2xl transition-colors duration-200 hover:rotate-90 transform" 
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="mb-6 text-center">
          <div className="mb-3">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full mb-3 shadow-lg">
              <span className="text-2xl animate-bounce">💕</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Welcome to VowAI
          </h2>
          <p className="text-gray-600 font-medium text-sm">Your AI-powered wedding planning assistant</p>
          <div className="w-20 h-1 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full mx-auto mt-2"></div>
        </div>

        <div className="flex mb-6 bg-gray-100 rounded-xl p-1 shadow-inner">
          <button 
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 transform ${
              activeTab === 'login' 
                ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg scale-105' 
                : 'text-gray-600 hover:text-rose-500 hover:bg-white hover:shadow-md'
            }`}
            onClick={() => switchTab('login')}
          >
            <span className="flex items-center justify-center">
              <span className="mr-2 animate-bounce">🔐</span>
              Login
            </span>
          </button>
          <button 
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 transform ${
              activeTab === 'signup' 
                ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg scale-105' 
                : 'text-gray-600 hover:text-rose-500 hover:bg-white hover:shadow-md'
            }`}
            onClick={() => switchTab('signup')}
          >
            <span className="flex items-center justify-center">
              <span className="mr-2 animate-bounce">✨</span>
              Sign Up
            </span>
          </button>
        </div>

        {errors.general && (
          <div className={`mb-6 p-4 rounded-xl border-l-4 ${
            errors.general.includes('successfully') 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 text-green-700' 
              : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-400 text-red-700'
          } shadow-sm`}>
            <div className="flex items-center">
              <span className="mr-2">{errors.general.includes('successfully') ? '✅' : '⚠️'}</span>
              {errors.general}
            </div>
          </div>
        )}

        {activeTab === 'login' && !showForgot ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">📧</span>
                Email
              </label>
              <input
                type="email"
                name="email"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  errors.email ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={loginData.email}
                onChange={handleLoginChange}
                placeholder="Enter your email address"
                required
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <span className="mr-1 animate-bounce">⚠️</span>
                  {errors.email}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">🔒</span>
                Password
              </label>
              <input
                type="password"
                name="password"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  errors.password ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={loginData.password}
                onChange={handleLoginChange}
                placeholder="Enter your password"
                required
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <span className="mr-1 animate-bounce">⚠️</span>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Forgot password link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setShowForgot(true); setErrors({}); setForgotEmail(loginData.email); }}
                className="text-sm font-medium text-rose-500 hover:text-rose-700 transition-colors duration-200 hover:underline"
              >
                Forgot password? 🔑
              </button>
            </div>
            
            <button 
              type="submit" 
              disabled={loading || !loginData.email || !loginData.password} 
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform shadow-lg ${
                loading || !loginData.email || !loginData.password
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 hover:scale-105 hover:shadow-xl active:scale-95'
              }`}
            >
              <span className="flex items-center justify-center">
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Logging in...
                  </>
                ) : (
                  <>
                    <span className="mr-2 animate-bounce">🚀</span>
                    Login to VowAI
                  </>
                )}
              </span>
            </button>
          </form>
        ) : activeTab === 'login' && showForgot ? (
          /* Forgot password view */
          <div className="space-y-4">
            {!forgotSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="text-center mb-2">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full mb-3">
                    <span className="text-2xl">🔑</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Reset Your Password</h3>
                  <p className="text-sm text-gray-500 mt-1">Enter your email and we'll send you a reset link</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <span className="mr-2 animate-pulse">📧</span>
                    Email
                  </label>
                  <input
                    type="email"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                      errors.forgotEmail ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                    }`}
                    value={forgotEmail}
                    onChange={(e) => { setForgotEmail(e.target.value.trim().toLowerCase()); setErrors({}); }}
                    placeholder="Enter your registered email"
                    required
                  />
                  {errors.forgotEmail && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <span className="mr-1 animate-bounce">⚠️</span>
                      {errors.forgotEmail}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !forgotEmail}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform shadow-lg ${
                    loading || !forgotEmail
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 hover:scale-105 hover:shadow-xl active:scale-95'
                  }`}
                >
                  <span className="flex items-center justify-center">
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">✉️</span>
                        Send Reset Link
                      </>
                    )}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setErrors({}); }}
                  className="w-full text-sm font-medium text-gray-500 hover:text-rose-500 transition-colors duration-200"
                >
                  ← Back to Login
                </button>
              </form>
            ) : (
              /* Success state after email sent */
              <div className="text-center space-y-4 py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full mb-2">
                  <span className="text-3xl animate-bounce">📬</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Check Your Email</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  If an account exists for <span className="font-semibold text-rose-600">{forgotEmail}</span>, we've sent a password reset link. Check your inbox (and spam folder).
                </p>
                <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-3 border border-rose-100">
                  <p className="text-xs text-gray-500">⏰ The link expires in 5 minutes</p>
                </div>
                <button
                  onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); setErrors({}); }}
                  className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <span className="mr-2">🔐</span>
                  Back to Login
                </button>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">👤</span>
                Full Name
              </label>
              <input
                type="text"
                name="name"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  errors.name ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={signupData.name}
                onChange={handleSignupChange}
                placeholder="Enter your full name"
                required
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1 animate-bounce">⚠️</span>
                  {errors.name}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">📧</span>
                Email
              </label>
              <input
                type="email"
                name="email"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  errors.email ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={signupData.email}
                onChange={handleSignupChange}
                placeholder="Enter your email address"
                required
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1 animate-bounce">⚠️</span>
                  {errors.email}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">🔒</span>
                Password
              </label>
              <input
                type="password"
                name="password"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  errors.password ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={signupData.password}
                onChange={handleSignupChange}
                placeholder="Create a strong password"
                required
              />
              
              {signupData.password && (
                <div className="mt-2 p-2 bg-gradient-to-r from-gray-50 to-rose-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">Strength:</span>
                    <span className={`font-bold ${passwordStrength < 60 ? 'text-red-600' : passwordStrength < 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${getPasswordStrengthColor()} shadow-sm`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {errors.password && (
                <div className="text-red-500 text-xs mt-1">
                  {Array.isArray(errors.password) ? (
                    <div className="bg-red-50 p-2 rounded-lg border border-red-200">
                      <p className="font-medium mb-1 flex items-center">
                        <span className="mr-1 animate-bounce">⚠️</span>
                        Requirements:
                      </p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {errors.password.map((error, index) => (
                          <li key={index} className="text-xs">{error}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="flex items-center">
                      <span className="mr-1 animate-bounce">⚠️</span>
                      {errors.password}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">🔐</span>
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  errors.confirmPassword ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={signupData.confirmPassword}
                onChange={handleSignupChange}
                onPaste={handlePaste}
                placeholder="Confirm your password"
                required
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1 animate-bounce">⚠️</span>
                  {errors.confirmPassword}
                </p>
              )}
            </div>
            
            <button 
              type="submit" 
              disabled={loading || !isSignupFormValid()} 
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform shadow-lg ${
                loading || !isSignupFormValid()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 hover:scale-105 hover:shadow-xl active:scale-95'
              }`}
            >
              <span className="flex items-center justify-center">
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <span className="mr-2 animate-bounce">✨</span>
                    Create VowAI Account
                  </>
                )}
              </span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
